"use client"

import { Box } from '@mui/material';
import { keyframes } from '@mui/system';
import Image from 'next/image';
import { Fragment, useEffect, useState } from 'react';
import { getLandingPage } from './api';
import RecentLinkHighlighter, { recentLinkHighlighterSx } from '@v2/components/RecentLinkHighlighter';
import { fullWidthLandingComponents, getLandingComponent } from './landingComponentRegistry';
import { ANCHOR_SCROLL_MARGIN, readAnchorId, scrollToHashTargetWhenSettled } from './anchor';
import { LandingPageComponent } from './types';
import { componentNodePath, StudioNodePath, studioNodeAttributes, useStudioNodePath } from '@v2/components/studio/context';

const pulse = keyframes`
  0%, 100% { opacity: 0.25; transform: scale(0.88); }
  50%       { opacity: 1;    transform: scale(1);    }
`;

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(10px); }
  to   { opacity: 1; transform: translateY(0);    }
`;
export default function LandingPageComponentLoader({
    components,
    params,
    nested = false,
    disableLinks = false,
    containerSx,
}: {
    components: LandingPageComponent[] | null;
    params?: { [key in string]: string }
    nested?: boolean
    disableLinks?: boolean
    containerSx?: Record<string, unknown>
}) {
    const [loading, setLoading] = useState(components === null)
    const [animate, setAnimate] = useState(false)
    const [internalComponents, setInternalComponents] = useState<LandingPageComponent[]>(components ?? [])
    // `null` on every public page; the studio frame provides "" at the root and a cell path
    // for the loaders nested inside a block.
    const studioParentPath = useStudioNodePath()

    useEffect(() => {
        if (components === null) {
            getLandingPage(params.slug).then((res) => {
                setInternalComponents(res.data.components)
                setLoading(false)
                setAnimate(true)
            })
        }
    }, [])

    // The browser's own jump to `#hash` happens before the components have settled — and
    // never happens at all when the page was fetched on the client. Re-run it once the
    // sections are on the page. Nested loaders share the same hash, so only the outer one
    // scrolls.
    useEffect(() => {
        if (nested || loading || !window.location.hash) return;
        return scrollToHashTargetWhenSettled(window.location.hash);
    }, [nested, loading])

    if (loading) return (
        <Box sx={{ height: '80vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Box sx={{ animation: `${pulse} 1.8s ease-in-out infinite` }}>
                <Image src="/assets/logo-micro.svg" alt="" width={56} height={56} />
            </Box>
        </Box>
    )

    const content = (
        <>
            {internalComponents.map((component, index) => {
                const Component = getLandingComponent(component.name);
                if (Component) {
                    const LandingComponent = Component as any;
                    const anchorId = readAnchorId(component.content?.settings);
                    const nodePath = componentNodePath(studioParentPath, index);
                    const rendered = <LandingComponent content={component.content} disableLinks={disableLinks} />;
                    return (
                        <Box
                            component={nested ? "div" : "section"}
                            key={component.id}
                            id={anchorId}
                            sx={{
                                // A block cell is a flex container, so a nested component is a flex
                                // item. Where the cell centres its children the item is shrink-to-fit,
                                // which the components in fullWidthLandingComponents cannot survive.
                                ...(nested && fullWidthLandingComponents.has(component.name) && { alignSelf: 'stretch' }),
                                // The studio frame has no fixed header of its own, so the offset the
                                // site header needs would leave an anchor stranded below the target.
                                ...(anchorId && { scrollMarginTop: nodePath === null ? ANCHOR_SCROLL_MARGIN : 0 }),
                            }}
                            {...studioNodeAttributes(nodePath, component.name, component.id)}
                        >
                            {nodePath === null ? rendered : <StudioNodePath path={nodePath}>{rendered}</StudioNodePath>}
                        </Box>
                    )
                } else {
                    return <Fragment key={component.id} />
                }
            })}
        </>
    );

    if (nested) {
        return (
            <Box component="div" width={1} sx={{ ...(animate && { animation: `${fadeIn} 0.45s ease-out` }), ...containerSx }}>
                {content}
            </Box>
        )
    }

    return (
        <RecentLinkHighlighter scope="landing" restoreScrollPosition>
            {({ ref, onClickCapture }) => (
            <Box
                ref={ref}
                component="main"
                width={1}
                onClickCapture={onClickCapture}
                sx={{
                    ...(animate && { animation: `${fadeIn} 0.45s ease-out` }),
                    ...recentLinkHighlighterSx,
                    ...containerSx,
                }}
            >
                {content}
            </Box>
            )}
        </RecentLinkHighlighter>
    )

}
